import React, { Component } from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { EQ } from '../comparison';
import { FILTER_TYPE } from '../const';

interface OptionType {
    value: string;
    label: string;
}

interface ReactMultiselectFilterProps {
    onFilter: (column: any, filterType: string) => (value: string[]) => void;
    column: {
        dataField: string;
        text: string;
    };
    options: Record<string, string>;
    id?: string;
    filterState?: {
        filterVal?: string[];
    };
    comparator?: typeof EQ;
    placeholder?: string;
    style?: React.CSSProperties;
    className?: string;
    withoutEmptyOption?: boolean;
    defaultValue?: string[];
    caseSensitive?: boolean;
    getFilter?: (callback: (filterVal: string[]) => void) => void;
    maxVisibleRows?: number;
}

interface ReactMultiselectFilterState {
    isSelected: boolean;
    selectedValues: string[];
}

function optionsEquals(
    currOpts: Record<string, string>,
    prevOpts: Record<string, string>
): boolean {
    const keys = Object.keys(currOpts);
    for (let i = 0; i < keys.length; i += 1) {
        if (currOpts[keys[i]] !== prevOpts[keys[i]]) {
            return false;
        }
    }
    return Object.keys(currOpts).length === Object.keys(prevOpts).length;
}

class ReactMultiselectFilter extends Component<
    ReactMultiselectFilterProps,
    ReactMultiselectFilterState
> {
    constructor(props: ReactMultiselectFilterProps) {
        super(props);
        this.filter = this.filter.bind(this);
        this.applyFilter = this.applyFilter.bind(this);
        const isSelected = (props.defaultValue || []).length > 0;
        this.state = {
            isSelected,
            selectedValues: this.getDefaultValue(),
        };
    }

    componentDidMount(): void {
        const { getFilter } = this.props;
        const value = this.state.selectedValues;
        if (value && value.length > 0) {
            this.applyFilter(value);
        }

        if (getFilter) {
            getFilter((filterVal: string[]) => {
                this.setState({ selectedValues: filterVal });
                this.applyFilter(filterVal);
            });
        }
    }

    componentDidUpdate(prevProps: ReactMultiselectFilterProps): void {
        let needFilter = false;
        if (this.props.defaultValue !== prevProps.defaultValue) {
            needFilter = true;
        } else if (!optionsEquals(this.props.options, prevProps.options)) {
            needFilter = true;
        }
        if (needFilter) {
            this.applyFilter(this.state.selectedValues);
        }
    }

    getDefaultValue(): string[] {
        const { filterState = {}, defaultValue = [] } = this.props;
        if (filterState && typeof filterState.filterVal !== 'undefined') {
            return filterState.filterVal;
        }
        return defaultValue;
    }

    getOptions(): OptionType[] {
        const { options } = this.props;
        const keys = Object.keys(options);

        const sortedKeys = keys.sort((a, b) => {
            if (a === 'NA') return -1;
            if (b === 'NA') return 1;

            const numA = parseFloat(a);
            const numB = parseFloat(b);

            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }

            return a.localeCompare(b);
        });

        return sortedKeys.map((key) => ({
            value: key,
            label: options[key],
        }));
    }

    cleanFiltered(): void {
        const value =
            this.props.defaultValue !== undefined
                ? this.props.defaultValue
                : [];
        this.setState({ selectedValues: value });
        this.applyFilter(value);
    }

    applyFilter(value: string[]): void {
        const filterValue = value || [];
        this.setState(() => ({ isSelected: filterValue.length > 0 }));
        this.props.onFilter(
            this.props.column,
            FILTER_TYPE.MULTISELECT
        )(filterValue);
    }

    filter(selectedOptions: MultiValue<OptionType>): void {
        const value = selectedOptions
            ? selectedOptions.map((option) => option.value)
            : [];
        this.setState({ selectedValues: value });
        this.applyFilter(value);
    }

    render(): JSX.Element {
        const {
            id = null,
            style = {},
            className = '',
            column,
            placeholder = 'Select...',
            maxVisibleRows = 3,
        } = this.props;

        const { selectedValues } = this.state;
        const selectOptions = this.getOptions();
        const selectedOptions = selectedValues
            .map((val) => selectOptions.find((opt) => opt.value === val))
            .filter((opt): opt is OptionType => Boolean(opt));

        const elmId = `react-select-multiselect-filter-column-${
            column.dataField
        }${id ? `-${id}` : ''}`;

        const rowHeight = 24;
        const containerPadding = 4;
        const maxHeight = maxVisibleRows * rowHeight + containerPadding;

        const customStyles: StylesConfig<OptionType, true> = {
            control: (provided, state) => ({
                ...provided,
                minHeight: '35px',
                boxShadow: 'none',
                borderColor: state.isFocused ? '#545cd8' : '#ced4da',
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                borderRadius: '4px',
                transition:
                    'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                '&:hover': {
                    borderColor: state.isFocused ? '#545cd8' : '#ced4da',
                },
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: '2px 4px',
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                flexWrap: 'wrap',
                gap: '2px',
                display: 'flex',
                alignItems: 'center',
                maxHeight: `${maxHeight}px`,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#c1c1c1 #f1f1f1',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                },
            }),
            placeholder: (provided) => ({
                ...provided,
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                fontWeight: 'normal',
                color: '#888f96',
                whiteSpace: 'nowrap',
                fontStyle: 'italic',
            }),
            input: (provided) => ({
                ...provided,
                margin: '0px',
                padding: '0px',
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                fontWeight: 'normal',
                color: '#888f96',
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                display: 'none',
            }),
            dropdownIndicator: (provided) => ({
                ...provided,
                display: 'none',
            }),
            clearIndicator: (provided) => ({
                ...provided,
                display: 'none',
            }),
            indicatorSeparator: (provided) => ({
                ...provided,
                display: 'none',
            }),
            menu: (provided) => ({
                ...provided,
                zIndex: 9,
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                marginTop: '4px',
                width: 'auto',
                minWidth: '100%',
                maxWidth: 'max-content',
            }),
            menuList: (provided) => ({
                ...provided,
                padding: '4px 0',
            }),
            option: (provided, state) => ({
                ...provided,
                fontSize: '14px',
                fontFamily: 'SegoeUI',
                backgroundColor: state.isSelected ? '#e5e8eb' : 'white',
                color: '#495057',
                cursor: 'pointer',
                padding: '8px 12px',
                whiteSpace: 'nowrap',
                ':active': {
                    backgroundColor: '#e5e8eb',
                },
            }),
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 9,
            }),
            multiValue: (provided) => ({
                ...provided,
                backgroundColor: '#e5e8eb',
                borderRadius: '4px',
                fontSize: '13px',
                margin: '1px',
                marginLeft: '2px',
                marginRight: '2px',
                border: '1px solid #e5e8eb',
                minHeight: '20px',
                display: 'inline-flex',
                alignItems: 'center',
            }),
            multiValueLabel: (provided) => ({
                ...provided,
                color: '#888f96',
                fontSize: '13px',
                fontFamily: 'SegoeUI-Semibold',
                padding: '2px 6px',
                paddingLeft: '6px',
                paddingRight: '2px',
                lineHeight: '1.2',
            }),
            multiValueRemove: (provided) => ({
                ...provided,
                color: '#888f96',
                backgroundColor: '#e5e8eb',
                paddingLeft: '2px',
                paddingRight: '5px',
                borderRadius: '0 3px 3px 0',
                ':hover': {
                    color: '#888f96',
                    cursor: 'pointer',
                },
            }),
        };

        return (
            <label
                className="filter-label"
                htmlFor={elmId}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}>
                <span className="sr-only">Filter by {column.text}</span>
                <Select<OptionType, true>
                    inputId={elmId}
                    isMulti
                    name={column.dataField}
                    options={selectOptions}
                    value={selectedOptions}
                    onChange={this.filter}
                    placeholder={placeholder}
                    styles={customStyles}
                    className={`react-select-container ${className}`}
                    classNamePrefix="react-select"
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable={true}
                    menuPortalTarget={document.body}
                    closeMenuOnScroll={true}
                    autoFocus={false}
                    blurInputOnSelect={false}
                    tabSelectsValue={false}
                    openMenuOnFocus={false}
                />
            </label>
        );
    }
}

export default ReactMultiselectFilter;
